<template>
  <li class="show-cell">
    <!-- Header -->
    <div class="show-header">
      <h3>
        <router-link
          :to="{ name: 'shows', params: { tourId: participation.show.tourId }, hash: '#' + participation.show.id }"
        >
          {{ participation.show.address.venue }}, {{ participation.show.address.city }}
        </router-link>
      </h3>
      <span class="show-date">{{ participation.show.showDate | dayjs.utc('MMM DD, YYYY (HH:mm)') }}</span>
    </div>

    <base-stack direction="row" justify="space-between" align="center">
      <!-- Badges -->
      <div class="badges" v-if="hasBadges">
        <span class="badge" v-if="participation.tickets.length > 0">
          {{ participation.tickets.length }} Ticket{{ participation.tickets.length > 1 ? 's' : '' }}
        </span>
        <span class="badge" v-if="validMgCount > 0">{{ validMgCount }} Secret Gathering</span>
        <span class="badge badge--early-entry" v-if="participation.hasEarlyEntry">Early Entry</span>
      </div>
      <action-menu v-if="hasConfirmedMg">
        <action-menu-button :loading="resending" @click="onResendMgEmail">
          Resend Secret Gathering email
        </action-menu-button>
      </action-menu>
    </base-stack>

    <!-- Event Timeline -->
    <ul class="timeline" v-if="events.length > 0">
      <li v-for="(event, index) in events" :key="index">
        <span class="event-date">{{ formatDate(event.date) }}</span>
        <span class="event-label">
          {{ event.label }}
          <router-link
            v-if="event.transactionId"
            :to="{ name: 'user-transactions', params: { id: userId }, hash: '#' + event.transactionId }"
            class="transaction-link"
          >
            View
          </router-link>
        </span>
      </li>
    </ul>
  </li>
</template>

<script lang="ts">
import ActionMenu from '@/components/ActionMenu.vue';
import ActionMenuButton from '@/components/ActionMenuButton.vue';
import { MeetAndGreetQuota, ShowParticipant } from '@/types/api';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import { Component, Prop, Vue } from 'vue-property-decorator';

interface TimelineEvent {
  date: Date;
  label: string;
  transactionId?: string;
}

const RESEND_MG_EMAIL_MUTATION = gql`
  mutation ResendMgEmail($input: ResendMeetAndGreetTicketConfirmationEmailInput!) {
    resendMeetAndGreetTicketConfirmationEmail(input: $input) {
      success
    }
  }
`;

@Component({
  components: { ActionMenu, ActionMenuButton },
})
export default class UserShowCell extends Vue {
  @Prop({ type: Object, required: true }) participation!: ShowParticipant;
  @Prop({ type: String, required: true }) userId!: string;

  resending = false;

  get hasBadges(): boolean {
    return (
      this.participation.tickets.length > 0 ||
      this.participation.meetAndGreetTickets.length > 0 ||
      this.participation.hasEarlyEntry
    );
  }

  get validMgCount(): number {
    return this.participation.meetAndGreetTickets
      .filter((mg) => !mg.declinedAt)
      .reduce((sum, mg) => sum + mg.isValidFor, 0);
  }

  get hasConfirmedMg(): boolean {
    return this.participation.meetAndGreetTickets.some((mg) => mg.confirmedAt && !mg.declinedAt);
  }

  get events(): TimelineEvent[] {
    const events: TimelineEvent[] = [];
    const p = this.participation;

    // Attendance events
    if (p.attendance) {
      const att = p.attendance;
      if (att.createdAt) {
        events.push({ date: new Date(att.createdAt), label: 'Registered for show' });
      }
      if (att.appliedEarlyEntryAt) {
        events.push({ date: new Date(att.appliedEarlyEntryAt), label: 'Applied for early entry' });
      }
      if (att.appliedMeetAndGreetAt) {
        events.push({ date: new Date(att.appliedMeetAndGreetAt), label: 'Applied for SG lottery' });
      }
      if (att.verifiedAt) {
        events.push({ date: new Date(att.verifiedAt), label: 'Verified at show' });
      }
    }

    // Ticket events
    for (const ticket of p.tickets) {
      if (ticket.createdAt) {
        const tierSuffix = ticket.tier ? ` (${ticket.tier.name})` : '';
        events.push({
          date: new Date(ticket.createdAt),
          label: `Ticket purchased${tierSuffix}`,
          transactionId: ticket.id,
        });
      }
      if (ticket.usedAt) {
        events.push({ date: new Date(ticket.usedAt), label: 'Ticket used', transactionId: ticket.id });
      }
      if (ticket.refundedAt) {
        events.push({ date: new Date(ticket.refundedAt), label: 'Ticket refunded', transactionId: ticket.id });
      }
    }

    // M&G ticket events
    for (const mg of p.meetAndGreetTickets) {
      const quotaLabel = this.formatQuota(mg.quota);

      if (mg.quota !== MeetAndGreetQuota.Purchase) {
        events.push({ date: new Date(mg.createdAt), label: `Secret Gathering invitation (${quotaLabel})` });

        if (mg.confirmedAt) {
          events.push({ date: new Date(mg.confirmedAt), label: `Secret Gathering invitation confirmed` });
        }
      }

      for (const purchase of mg.purchases || []) {
        if (purchase.date) {
          events.push({
            date: new Date(purchase.date),
            label: `Secret Gathering purchased (${purchase.type === 'Ticket' ? `${purchase.quantity} tickets` : 'bundle'})`,
            transactionId: purchase.id,
          });
        }
        if (purchase.refundedAt) {
          events.push({
            date: new Date(purchase.refundedAt),
            label: `Secret Gathering purchase refunded (${purchase.type === 'Ticket' ? `${purchase.quantity} tickets` : 'bundle'})`,
            transactionId: purchase.id,
          });
        }
      }
      if (mg.declinedAt) {
        events.push({ date: new Date(mg.declinedAt), label: 'Secret Gathering declined' });
      }
      if (mg.usedAt) {
        events.push({ date: new Date(mg.usedAt), label: 'Secret Gathering used' });
      }
    }

    return events.sort((a, b) => b.date.getTime() - a.date.getTime());
  }

  formatQuota(quota: MeetAndGreetQuota): string {
    switch (quota) {
      case MeetAndGreetQuota.Purchase:
        return 'Purchased';
      case MeetAndGreetQuota.Loyal:
        return 'Loyal';
      case MeetAndGreetQuota.Random:
        return 'Random';
      default:
        return quota;
    }
  }

  formatDate(date: Date): string {
    return dayjs(date).format('MMM DD, YYYY HH:mm');
  }

  async onResendMgEmail() {
    const confirmedMg = this.participation.meetAndGreetTickets.find((mg) => mg.confirmedAt && !mg.declinedAt);
    if (!confirmedMg) return;

    this.resending = true;
    try {
      await this.$apollo.mutate({
        mutation: RESEND_MG_EMAIL_MUTATION,
        variables: {
          input: { meetAndGreetTicketId: confirmedMg.id },
        },
      });
      this.$modal.alert({
        title: 'Email sent',
        message: 'A new secret gathering confirmation email has been sent to the user. Check "Emails" tab for details.',
      });
    } catch (error) {
      this.$modal.alert({ title: 'Error', message: 'Failed to send email: ' + (error as Error).message });
    } finally {
      this.resending = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.show-cell {
  border: 1px solid $color-theme-red;
  padding: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.show-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-weight: bold;

    a {
      color: $color-theme-red;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .show-date {
    color: #666;
    font-size: 0.9em;
  }
}

.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 12px;
  align-items: center;
}

.badge {
  display: inline-block;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: bold;
  font-family: monospace;
  color: $color-theme-yellow;
  border: 1px solid $color-theme-red;

  &--early-entry {
    border-color: $color-theme-yellow;
  }
}

.timeline {
  border-top: 1px solid $color-theme-yellow;
  list-style: none;
  padding: 0;
  padding-top: 12px;
  margin: 0;

  li {
    display: flex;
    gap: 16px;
    padding: 6px 0;
    font-size: 0.85em;
    border-bottom: 1px solid #333;

    &:last-child {
      border-bottom: none;
    }
  }
}

.event-date {
  flex-shrink: 0;
  width: 140px;
  color: $color-theme-yellow;
  font-family: monospace;
}

.event-label {
  color: $color-theme-yellow;
}

.transaction-link {
  margin-left: 8px;
  color: $color-theme-red;
  font-size: 0.9em;

  &:hover {
    text-decoration: underline;
  }
}
</style>
